import { useState } from "react";
import {
  Button,
  Card,
  Eyebrow,
  Icons,
  Modal,
  PinDisplay,
  PinInput,
  Progress,
  SatelliteButton,
  Status,
  useToast,
} from "../design/components/index.js";

/**
 * Lebender Styleguide.
 *
 * Zeigt jede Komponente in jedem Zustand, damit Abweichungen vom Design-System
 * auffallen, bevor sie sich ueber die Anwendung verteilen. Bewusst eine echte
 * Route statt Storybook: kein zusaetzliches Werkzeug, keine zweite Build-Kette,
 * und die Komponenten laufen exakt in der Umgebung, in der sie spaeter arbeiten.
 */
export function Styleguide(): React.ReactElement {
  const toast = useToast();
  const [modalOpen, setModalOpen] = useState(false);
  const [blockingOpen, setBlockingOpen] = useState(false);
  const [pin, setPin] = useState("");
  const [totp, setTotp] = useState("");
  const [totpError, setTotpError] = useState<string | undefined>(undefined);

  return (
    <div className="container section">
      <Eyebrow>Design-System</Eyebrow>
      <h1 style={{ marginTop: "var(--space-2)" }}>Styleguide</h1>
      <p style={{ marginTop: "var(--space-3)", color: "var(--color-granite)" }}>
        Alle Komponenten in allen Zuständen. Abweichungen von{" "}
        <code>DESIGN-mastercard.md</code> sind in <code>docs/design-conflicts.md</code>{" "}
        begründet.
      </p>

      <Section title="Typografie">
        <h1>Überschrift 1</h1>
        <h2>Überschrift 2</h2>
        <h3>Überschrift 3</h3>
        <h4>Überschrift 4</h4>
        <Eyebrow>Eyebrow-Label</Eyebrow>
        <p>
          Fließtext im Gewicht 450. Dieses Halbgewicht ist identitätstragend: weicher als
          500, fester als 400. Ersetzt man es durch 400, verliert der Text seinen Ton.
        </p>
        <p style={{ color: "var(--color-granite)" }}>
          Sekundärer Fließtext in Granite — für erläuternde Absätze unter einer
          Überschrift.
        </p>
      </Section>

      <Section title="Buttons">
        <Row>
          <Button variant="primary">Verbinden</Button>
          <Button variant="secondary">Abbrechen</Button>
          <Button variant="danger">Ablehnen</Button>
          <Button variant="ghost">Später</Button>
          <Button variant="consent">Einwilligen</Button>
        </Row>
        <Row>
          <Button variant="primary" disabled>
            Deaktiviert
          </Button>
          <Button variant="secondary" disabled>
            Deaktiviert
          </Button>
        </Row>
        <Row>
          <Button variant="primary" large>
            Große Aktion
          </Button>
        </Row>
        <Row>
          <SatelliteButton label="Weiter zu den Details" />
        </Row>
        <p style={{ fontSize: "var(--text-small)", color: "var(--color-granite)" }}>
          Orange ist der Einwilligungsfarbe vorbehalten. Für gewöhnliche Aktionen wäre es
          ein falsches Signal.
        </p>
      </Section>

      <Section title="Statusanzeigen">
        <Row>
          <Status kind="connected">Verbunden</Status>
          <Status kind="waiting">Warte auf Bestätigung</Status>
          <Status kind="error">Verbindung verloren</Status>
        </Row>
        <p style={{ fontSize: "var(--text-small)", color: "var(--color-granite)" }}>
          Der farbige Punkt trägt keine Bedeutung allein — daneben steht immer Text.
        </p>
      </Section>

      <Section title="PIN: Anzeige">
        <PinDisplay pin="0421" label="Deine PIN" />
        <p style={{ fontSize: "var(--text-small)", color: "var(--color-granite)" }}>
          Führende Nullen bleiben erhalten, weil die PIN als Zeichenkette behandelt wird.
        </p>
      </Section>

      <Section title="PIN: Eingabe (vierstellig)">
        <PinInput
          length={4}
          value={pin}
          onChange={setPin}
          label="PIN des anderen Geräts"
          description="Vier Ziffern. Einfügen aus der Zwischenablage füllt alle Felder."
        />
      </Section>

      <Section title="TOTP: Eingabe (sechsstellig, mit Fehler)">
        <PinInput
          length={6}
          value={totp}
          onChange={(next) => {
            setTotp(next);
            setTotpError(undefined);
          }}
          onComplete={(code) => {
            // Zur Vorführung: gerade Endziffern gelten als gültig.
            if (Number(code.at(-1)) % 2 !== 0) {
              setTotpError("Code ungültig oder abgelaufen.");
              // Direkt zurücksetzen statt über onChange -- sonst würde der
              // Handler oben den soeben gesetzten Fehler gleich wieder löschen.
              setTotp("");
            } else {
              toast.show("Code akzeptiert.", "success");
            }
          }}
          label="Sechsstelliger Code aus der Authenticator-App"
          description="Endet der Code auf eine ungerade Ziffer, zeigt die Vorführung den Fehlerzustand."
          error={totpError}
        />
      </Section>

      <Section title="Fortschritt">
        <Progress value={0} label="Wartet" detail="0 von 4,0 GB" />
        <Progress value={0.42} label="Überträgt urlaub-2026.zip" detail="1,7 von 4,0 GB" />
        <Progress value={1} label="Abgeschlossen" detail="4,0 GB · SHA-256 geprüft" />
        <Progress label="Prüfsumme wird berechnet" />
      </Section>

      <Section title="Karten">
        <div style={{ display: "grid", gap: "var(--space-2)", gridTemplateColumns: "repeat(auto-fit, minmax(260px, 1fr))" }}>
          <Card>
            <Eyebrow>Standard</Eyebrow>
            <h3 style={{ marginTop: "var(--space-1)" }}>Lifted Cream</h3>
            <p style={{ marginTop: "var(--space-1)", color: "var(--color-granite)" }}>
              Papier auf Papier, ohne Schatten.
            </p>
          </Card>
          <Card variant="raised">
            <Eyebrow>Erhoben</Eyebrow>
            <h3 style={{ marginTop: "var(--space-1)" }}>Weiß mit Halo</h3>
            <p style={{ marginTop: "var(--space-1)", color: "var(--color-granite)" }}>
              48px Weichzeichnung bei 8 % Deckkraft.
            </p>
          </Card>
        </div>
      </Section>

      <Section title="Modale">
        <Row>
          <Button onClick={() => setModalOpen(true)}>Wegklickbar öffnen</Button>
          <Button variant="secondary" onClick={() => setBlockingOpen(true)}>
            Entscheidung erzwingen
          </Button>
        </Row>

        <Modal
          open={modalOpen}
          onClose={() => setModalOpen(false)}
          title="Datei verwerfen?"
          actions={
            <>
              <Button variant="danger" onClick={() => setModalOpen(false)}>
                Verwerfen
              </Button>
              <Button variant="secondary" onClick={() => setModalOpen(false)}>
                Behalten
              </Button>
            </>
          }
        >
          <p>Escape und ein Klick auf den Hintergrund schließen dieses Modal.</p>
        </Modal>

        <Modal
          open={blockingOpen}
          onClose={() => setBlockingOpen(false)}
          dismissible={false}
          title="Eingehende Verbindung"
          actions={
            <>
              <Button onClick={() => setBlockingOpen(false)}>Annehmen</Button>
              <Button variant="danger" onClick={() => setBlockingOpen(false)}>
                Ablehnen
              </Button>
            </>
          }
        >
          <p>
            Ruhiger Falke möchte sich verbinden. Escape und Hintergrundklick sind hier
            wirkungslos — versehentliches Wegklicken darf kein „Ablehnen“ sein.
          </p>
        </Modal>
      </Section>

      <Section title="Meldungen">
        <Row>
          <Button variant="secondary" onClick={() => toast.show("Verbindung hergestellt.", "success")}>
            Erfolg
          </Button>
          <Button variant="secondary" onClick={() => toast.show("Die PIN ist abgelaufen.", "error")}>
            Fehler
          </Button>
          <Button variant="secondary" onClick={() => toast.show("Datei wird vorbereitet.")}>
            Hinweis
          </Button>
        </Row>
      </Section>

      <Section title="Symbole">
        <Row>
          {Object.entries(Icons).map(([name, Icon]) => (
            <span
              key={name}
              style={{
                display: "inline-flex",
                alignItems: "center",
                gap: "var(--space-1)",
                fontSize: "var(--text-small)",
                color: "var(--color-granite)",
              }}
            >
              <Icon size={20} />
              {name}
            </span>
          ))}
        </Row>
      </Section>

      <Section title="Farbflächen">
        <div style={{ display: "grid", gap: "var(--space-1)", gridTemplateColumns: "repeat(auto-fit, minmax(160px, 1fr))" }}>
          <Swatch name="Canvas Cream" value="#F3F0EE" note="Seitenhintergrund" />
          <Swatch name="Lifted Cream" value="#FCFBFA" note="gehobene Fläche" />
          <Swatch name="Ink Black" value="#141413" note="Text, primärer CTA" onDark />
          <Swatch name="Slate Gray" value="#696969" note="4,84:1 — AA" onDark />
          <Swatch name="Granite" value="#555555" note="6,57:1 — AA" onDark />
          <Swatch name="Signal Orange" value="#CF4500" note="nur als Fläche" onDark />
          <Swatch name="Light Signal" value="#F37338" note="nur Dekoration" />
          <Swatch name="Clay Brown" value="#9A3A0A" note="6,20:1 — AA" onDark />
          <Swatch name="Link Blue" value="#3860BE" note="5,16:1 — AA" onDark />
          <Swatch name="Success" value="#1F6B3F" note="5,72:1 — AA" onDark />
          <Swatch name="Danger" value="#A12118" note="6,74:1 — AA" onDark />
          <Swatch name="Dust Taupe" value="#D1CDC7" note="1,40:1 — nur Dekoration" />
        </div>
      </Section>

      <Section title="Radien">
        <Row>
          {[
            ["6px", "var(--radius-micro)"],
            ["20px", "var(--radius-button)"],
            ["24px", "var(--radius-consent)"],
            ["40px", "var(--radius-stadium)"],
            ["999px", "var(--radius-pill)"],
          ].map(([label, token]) => (
            <div key={label} style={{ textAlign: "center" }}>
              <div
                style={{
                  width: 120,
                  height: 80,
                  background: "var(--color-white)",
                  borderRadius: token,
                  boxShadow: "var(--shadow-1)",
                }}
              />
              <span style={{ fontSize: "var(--text-small)", color: "var(--color-granite)" }}>
                {label}
              </span>
            </div>
          ))}
        </Row>
        <p style={{ fontSize: "var(--text-small)", color: "var(--color-granite)" }}>
          Die Skala überspringt 8–16px bewusst. Dazwischen wirkt es generisch.
        </p>
      </Section>

      <Section title="Erhebung">
        <Row>
          {[
            ["Stufe 1", "var(--shadow-1)"],
            ["Stufe 2", "var(--shadow-2)"],
            ["Stufe 3", "var(--shadow-3)"],
          ].map(([label, shadow]) => (
            <div key={label} style={{ textAlign: "center" }}>
              <div
                style={{
                  width: 140,
                  height: 90,
                  background: "var(--color-white)",
                  borderRadius: "var(--radius-stadium)",
                  boxShadow: shadow,
                }}
              />
              <span style={{ fontSize: "var(--text-small)", color: "var(--color-granite)" }}>
                {label}
              </span>
            </div>
          ))}
        </Row>
      </Section>
    </div>
  );
}

function Section({ title, children }: { title: string; children: React.ReactNode }): React.ReactElement {
  return (
    <section style={{ marginTop: "var(--space-8)" }}>
      <h2 style={{ fontSize: "var(--text-h3)", paddingBottom: "var(--space-1)", borderBottom: "1px solid var(--color-dust)" }}>
        {title}
      </h2>
      <div style={{ marginTop: "var(--space-3)", display: "flex", flexDirection: "column", gap: "var(--space-3)" }}>
        {children}
      </div>
    </section>
  );
}

function Row({ children }: { children: React.ReactNode }): React.ReactElement {
  return (
    <div style={{ display: "flex", flexWrap: "wrap", gap: "var(--space-2)", alignItems: "center" }}>
      {children}
    </div>
  );
}

function Swatch({
  name,
  value,
  note,
  onDark = false,
}: {
  name: string;
  value: string;
  note: string;
  onDark?: boolean;
}): React.ReactElement {
  return (
    <div style={{ borderRadius: "var(--radius-button)", overflow: "hidden", background: "var(--color-white)" }}>
      <div
        style={{
          background: value,
          height: 72,
          display: "flex",
          alignItems: "flex-end",
          padding: "var(--space-1)",
          color: onDark ? "#fff" : "var(--color-ink)",
          fontSize: "var(--text-micro)",
          fontVariantNumeric: "tabular-nums",
        }}
      >
        {value}
      </div>
      <div style={{ padding: "var(--space-1)" }}>
        <div style={{ fontSize: "var(--text-small)", fontWeight: "var(--weight-display)" }}>{name}</div>
        <div style={{ fontSize: "var(--text-micro)", color: "var(--color-granite)" }}>{note}</div>
      </div>
    </div>
  );
}
